import { QUERY, useQueryOperation } from 'api/Query';
import clsx from 'clsx';
import type { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import type { JSX } from 'react';
import { useKeyboardShortcut } from 'ts/base/hooks/UseKeyboardShortcut';
import { TeamscaleLink } from 'ts/base/routing/TeamscaleLink';
import { KeyboardShortcutRegistry } from 'ts/base/scaffolding/KeyboardShortcutRegistry';
import { useCodeTokenFocus } from 'ts/commons/code/CodeToken.focus';
import { useCodeTokenHighlighting } from 'ts/commons/code/CodeToken.highlighting';
import { useCodeTokenRefactoringSuggestion } from 'ts/commons/code/CodeToken.refactoring';
import type { ExpandedMacroElementInfo } from 'ts/commons/code/ExpandedMacroElementInfo';
import { highlightCodeSearch } from 'ts/commons/code/InCodeSearchUtils';
import { IssueLikeReferencePopup } from 'ts/commons/code/IssueLikeReferencePopup';
import { RenderedExpandedMacroCode } from 'ts/commons/code/RenderedCode';
import { useRenderedCodeOptions } from 'ts/commons/code/RenderedCode.context';
import { isCodeLineSegment, type LineSegment } from 'ts/commons/code/RenderedCode.segments';
import { StringUtils } from 'ts/commons/StringUtils';
import { UIUtils } from 'ts/commons/UIUtils';
import { Icon } from 'ts/components/Icon';
import { Popup } from 'ts/components/Popup';
import { ELanguage } from 'typedefs/ELanguage';
import type { ElementLocation } from 'typedefs/ElementLocation';
import type { IssueReference } from 'typedefs/IssueReference';
import type { SinglePreprocessorExpansionTransport } from 'typedefs/SinglePreprocessorExpansionTransport';
import { Links } from '../links/Links';
import style from './RenderedCode.module.less';

type CodeTokenProps = {
	lineNumber: number;
	segment: LineSegment;
	issueReferences: IssueReference[];
};

/** A single code token within a source line. */
export function CodeToken({ segment, issueReferences, lineNumber }: CodeTokenProps): JSX.Element {
	const {
		project,
		preprocessorExpansions,
		codeElement,
		uniformPath,
		commit,
		language,
		expansionGroupStartOffsets,
		highlightMacros,
		searchResults,
		highlightedSearchResult,
		hideOutline,
		imperativeHandleRef,
		onNavigateToDefinition
	} = useRenderedCodeOptions();

	const expansionGroupId = preprocessorExpansions?.offsetToExpansionGroup[segment.offsetInFile];
	const {
		isHovered,
		isExpandedExpansionGroup,
		handleMouseLeave,
		handleMouseEnter,
		toggleGroupExpansion,
		isExpansionGroupHovered
	} = useCodeTokenHighlighting(segment, expansionGroupId);
	const [isFocused, toggleEntityFocus] = useCodeTokenFocus(segment);
	const refactoringSuggestion = useCodeTokenRefactoringSuggestion(segment);

	const isFirstTokenOfExpansionGroup = expansionGroupStartOffsets.has(segment.offsetInFile);

	const cssClass = isCodeLineSegment(segment) ? segment.cssStyleClass : '';
	const canBeHighlighted =
		!hideOutline &&
		((isCodeLineSegment(segment) && segment.isIdentifier) || Boolean(segment.associatedBracketSegmentId));

	const isHighlighted = isHovered || isExpansionGroupHovered || isFocused;

	const expandedMacro = useDisplayTextForToken(
		segment.text,
		isExpandedExpansionGroup,
		project,
		uniformPath,
		commit,
		isFirstTokenOfExpansionGroup,
		expansionGroupId
	);
	const tokenDisplayText = getDisplayTextForToken(
		segment.text,
		expandedMacro?.replacementText,
		isExpandedExpansionGroup,
		isFirstTokenOfExpansionGroup
	);

	const renderableMacro: ExpandedMacroElementInfo = {
		codeLinks: {},
		text: tokenDisplayText,
		uniformPath,
		issueReferences,
		language: language?.name,
		tokens: expandedMacro?.tokensOffsetsAndStyles ?? []
	};

	let textElement = highlightCodeSearch(segment, searchResults, highlightedSearchResult, tokenDisplayText);
	if (language === ELanguage.KUBERNETES) {
		// Adjust the text to contain html links to the referenced source files from full manifests in Kubernetes Helm
		// charts or Kustomizations. For now, we go with this rather hacky approach of patching an anchor element into the text.
		// This breaks the highlighting of search results. Later, we may want to include this into the existing code links mechanism, see FormattedTokenElementInfo.codeLinks.
		// See TS-36722.
		const searchStringHelm = '# Source: ';
		const searchStringKustomize = 'path: ';
		if (tokenDisplayText.startsWith(searchStringHelm)) {
			textElement = getReferenceForHelm(tokenDisplayText, searchStringHelm, project, uniformPath);
		} else if (tokenDisplayText.includes(searchStringKustomize)) {
			textElement = getReferenceForKustomize(tokenDisplayText, searchStringKustomize, project, uniformPath);
		}
	}

	useKeyboardShortcut(
		'+',
		'Expand macro',
		() => {
			if (!isExpandedExpansionGroup) {
				toggleGroupExpansion();
			}
		},
		expansionGroupId !== undefined,
		false
	);
	useKeyboardShortcut(
		'-',
		'Undo macro expansion',
		() => {
			if (isExpandedExpansionGroup) {
				toggleGroupExpansion();
			}
		},
		expansionGroupId !== undefined,
		false
	);

	// The check for empty whitespace(s) is necessary to prevent them rendered before the actual expanded macro text.
	// Note that the expanded macro text will not be affected as they are received concatenated from the service call.
	const renderMarcoExpansion = isExpandedExpansionGroup && !StringUtils.isEmptyOrWhitespace(tokenDisplayText);

	const overwriteSearchResults = [];
	let overwriteHighlightedSearchResult = -1;
	if (renderMarcoExpansion) {
		for (let i = 0; i < searchResults.length; i++) {
			const macroMatch = searchResults[i]?.macroMatches.get(segment.offsetInFile);
			if (!macroMatch) {
				continue;
			}
			if (highlightedSearchResult === i) {
				overwriteHighlightedSearchResult = overwriteSearchResults.length;
			}
			overwriteSearchResults.push({
				...macroMatch,
				line: lineNumber,
				macroMatches: new Map()
			});
		}
	}

	return (
		<span
			key={`${segment.fileAndOffsetId}${expansionGroupId ?? -1}${String(isExpandedExpansionGroup)}${String(
				highlightMacros
			)}`}
			{...((canBeHighlighted || expansionGroupId != null) && {
				onMouseEnter: handleMouseEnter,
				onMouseLeave: handleMouseLeave,
				onClick: e => {
					e.preventDefault();
					if (!segment.associatedBracketSegmentId) {
						let codeLinks: Record<number, ElementLocation> = {};
						if ('codeLinks' in codeElement) {
							codeLinks = codeElement.codeLinks;
						}
						const codeLink = codeLinks[segment.offsetInFile];
						if (UIUtils.isCtrlKey(e) && codeLink && onNavigateToDefinition) {
							onNavigateToDefinition(
								project,
								codeElement.uniformPath,
								codeLink,
								imperativeHandleRef.current
							);
							return;
						}
					}
					toggleEntityFocus();
				}
			})}
			{...(expansionGroupId !== undefined && {
				onMouseEnter: handleMouseEnter,
				onMouseLeave: handleMouseLeave
			})}
			className={clsx(cssClass + ' flex-row', {
				[style.codeHighlight!]: isHighlighted,
				'cursor-pointer': isHighlighted,
				'code-input-param-highlight': refactoringSuggestion.isInputParameter,
				'code-return-param-highlight': refactoringSuggestion.isReturnParameter,
				relative: isHovered && expansionGroupId != null,
				'hover:!text-green-800 hover:font-semibold':
					'codeLinks' in codeElement && codeElement.codeLinks[segment.offsetInFile],
				'!text-purple-500': highlightMacros && expansionGroupId != null
			})}
			title={refactoringSuggestion.title}
			data-code-highlight={isHighlighted || undefined}
			data-token-offset={segment.offsetInFile}
			{...('codeLinks' in codeElement &&
				codeElement.codeLinks[segment.offsetInFile] &&
				onNavigateToDefinition && {
					title: `Go to definition (${KeyboardShortcutRegistry.CMD_CTRL} + Click)`
				})}
		>
			{issueReferences.length > 0 ? (
				<IssueLikeReferencePopup
					triggerText={tokenDisplayText}
					issueReferences={issueReferences}
					project={project}
				/>
			) : renderMarcoExpansion ? (
				<RenderedExpandedMacroCode
					project={project}
					codeElement={renderableMacro}
					commit={commit}
					findings={[]}
					hideBorder
					hideContextMenu
					overwriteSearchResults={overwriteSearchResults}
					highlightedSearchResult={overwriteHighlightedSearchResult}
				/>
			) : (
				textElement
			)}
			{highlightMacros && isFirstTokenOfExpansionGroup ? (
				<ToggleExpansionGroupButton
					dataTestId={'expansion-group-button-' + segment.offsetInFile}
					isExpanded={isExpandedExpansionGroup}
					onToggleExpand={toggleGroupExpansion}
				/>
			) : null}
		</span>
	);
}

function getReferenceForHelm(text: string, searchStringHelm: string, project: string, uniformPath: string) {
	// For Helm the source reference looks as follows:
	// # Source:  airflow/charts/postgresql/templates/secrets.yaml
	// The chart name is prepended to the path, the actual path in Teamscale starts after that,
	// i.e. after "airflow" in this case, so we drop the first path element.
	const startIndexOfPath = text.indexOf(searchStringHelm) + searchStringHelm.length;
	const subPath = text.substring(startIndexOfPath);
	const subPathWithoutChartName = subPath.substring(subPath.indexOf('/') + 1);
	const relativeMetricsLink = Links.code(
		project,
		uniformPath.substring(0, uniformPath.lastIndexOf('/') + 1) + subPathWithoutChartName
	);
	return (
		<>
			{searchStringHelm} <TeamscaleLink to={relativeMetricsLink}>{subPath}</TeamscaleLink>
		</>
	);
}

function getReferenceForKustomize(text: string, searchStringKustomize: string, project: string, uniformPath: string) {
	const startIndexOfPath = text.indexOf(searchStringKustomize) + searchStringKustomize.length;
	const prefix = text.substring(0, startIndexOfPath);

	// TODO: We should properly resolve `..` in the subPath. This works currently, but is displayed as `..`
	// in the breadcrumb instead of the resolved path which may not be ideal. See TS-36041.
	const subPath = text.substring(startIndexOfPath);
	const relativeMetricsLink = Links.code(
		project,
		uniformPath.substring(0, uniformPath.lastIndexOf('/') + 1) + subPath
	);
	return (
		<>
			{prefix} <TeamscaleLink to={relativeMetricsLink}>{subPath}</TeamscaleLink>
		</>
	);
}

/**
 * Returns the text that should be displayed for the given token. Usually, that is just the token text.
 *
 * For tokens that can be replaced by preprocessor expansions, this can return the expanded text.
 *
 * @param tokenText The "normal" text of the token
 * @param isExpandedExpansionGroup Whether the token is part of a preprocessor expansions group (i.e., of the tokens
 *   that are replaced by an expansion)
 * @param project Current Teamscale project
 * @param uniformPath Current file
 * @param commit Current commit
 * @param isFirstTokenOfMacro Whether the given token is the first token of an expansion group (the first one is
 *   replaced by the expansion's replacement text, the other tokens of the group are replaced by '').
 * @param expansionGroupId If of the preprocessor expansion group that this token is part of
 */
function useDisplayTextForToken(
	tokenText: string,
	isExpandedExpansionGroup: boolean,
	project: string,
	uniformPath: string,
	commit: UnresolvedCommitDescriptor,
	isFirstTokenOfMacro: boolean,
	expansionGroupId?: number
): SinglePreprocessorExpansionTransport | undefined {
	const initialTransport: SinglePreprocessorExpansionTransport = {
		offsetsOfReplacedOriginalTokens: [],
		replacementText: tokenText,
		tokensOffsetsAndStyles: []
	};

	return useQueryOperation(
		QUERY.getSinglePreprocessorExpansion(project, uniformPath, {
			expansionGroupIds: [expansionGroupId!],
			t: commit
		}),
		{
			enabled: expansionGroupId != null && isExpandedExpansionGroup && isFirstTokenOfMacro,
			initialData: () => [initialTransport]
		}
	).data![0];
}

function getDisplayTextForToken(
	tokenText: string,
	expandedMacroText: string | undefined,
	isExpandedExpansionGroup: boolean,
	isFirstTokenOfMacro: boolean
): string {
	if (isExpandedExpansionGroup && !isFirstTokenOfMacro) {
		return '';
	}
	return isExpandedExpansionGroup
		? expandedMacroText ||
				// Use space when macro is expanded to the empty string. This ensures that the element can be selected
				' '
		: tokenText;
}

type ToggleExpandMacroButtonProps = {
	isExpanded: boolean;
	onToggleExpand: () => void;
	dataTestId: string;
};

function ToggleExpansionGroupButton({
	onToggleExpand,
	isExpanded,
	dataTestId
}: ToggleExpandMacroButtonProps): JSX.Element {
	return (
		<Popup
			trigger={
				<Icon
					name={isExpanded ? 'minus' : 'plus'}
					circular
					inverted
					color="blue"
					size="tiny"
					data-testid={dataTestId}
					onClick={(e: React.MouseEvent) => {
						e.preventDefault();
						e.stopPropagation();
						onToggleExpand();
					}}
					className="!ml-1 cursor-pointer relative !align-text-top !box-border"
				/>
			}
		>
			{isExpanded ? 'Collapse macro' : 'Expand macro'}
			<br />
			<span className="text-gray-500">
				Shortcut <span className="shortcut">{isExpanded ? '-' : '+'}</span>
			</span>
		</Popup>
	);
}
